"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useState } from "react";
export default function BookingForm() {
  return <section className="bg-white py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <FormProvider className="w-full" formId="consultation-booking-form" sectionName="booking-form-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        service: "",
        date: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          minLength: 10
        },
        service: {
          required: true
        },
        date: {
          required: true
        }
      }} submitText="Book Consultation" successMessage="Your consultation has been booked successfully! We will contact you shortly.">
          <BookingFormContent />
        </FormProvider>
      </div>
    </section>;
}
function BookingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="max-w-2xl mx-auto p-8 bg-primary/10 border border-primary/30 rounded-lg text-center">
        <p className="text-foreground text-lg mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Book Another Consultation
        </Button>
      </div>;
  }
  return <div ref={formRef}>
      <div className="text-center mb-12">
        <h2 className="text-4xl font-display font-bold mb-4 !text-[hsl(22,78%,26%)]" style={{
        opacity: formVisible ? 1 : 0,
        transform: formVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1)"
      }}>
          Book a Consultation
        </h2>
        <p className="text-muted-foreground text-lg" style={{
        opacity: formVisible ? 1 : 0,
        transform: formVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 50ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 50ms"
      }}>
          Schedule your consultation with our expert auditing team
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {}
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            }
          }} render={({
            field
          }) => <FormItem style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 100ms"
          }}>
                  <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(22,78%,26%)]">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your full name" {...field} className="border-border focus:border-primary transition-all duration-[120ms] focus:bg-white/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 150ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 150ms"
          }}>
                  <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(22,78%,26%)]">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your.email@example.com" {...field} className="border-border focus:border-primary transition-all duration-[120ms] focus:bg-white/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            minLength: {
              value: 10,
              message: "Phone number must be at least 10 digits"
            }
          }} render={({
            field
          }) => <FormItem style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 200ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 200ms"
          }}>
                  <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(22,78%,26%)]">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+971 4 123 4567" {...field} className="border-border focus:border-primary transition-all duration-[120ms] focus:bg-white/50" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className="space-y-6">
            <FormField control={control} name="service" rules={{
            required: "Please select a service"
          }} render={({
            field
          }) => <FormItem style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 250ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 250ms"
          }}>
                  <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(22,78%,26%)]">Service Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full border-border focus:border-primary transition-all duration-[120ms] focus:bg-white/50">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="statutory-audit">Statutory Audit</SelectItem>
                      <SelectItem value="internal-audit">Internal Audit</SelectItem>
                      <SelectItem value="liquidation-report">Liquidation Report</SelectItem>
                      <SelectItem value="corporate-tax">Corporate Tax Compliance</SelectItem>
                      <SelectItem value="vat-registration">VAT Registration</SelectItem>
                      <SelectItem value="tax-residency">Tax Residency Certificate</SelectItem>
                      <SelectItem value="feasibility-study">Feasibility Study</SelectItem>
                      <SelectItem value="bookkeeping">Bookkeeping</SelectItem>
                      <SelectItem value="financial-forecasting">Financial Forecasting</SelectItem>
                      <SelectItem value="esr-compliance">ESR Compliance</SelectItem>
                      <SelectItem value="aml-compliance">AML Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem style={{
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 300ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 300ms"
          }}>
                  <FormLabel className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 data-[error=true]:text-destructive !text-[hsl(22,78%,26%)]">Preferred Date</FormLabel>
                  <FormControl>
                    <div>
                      <Button type="button" variant="outline" className="w-full justify-start text-left font-normal border-border focus:border-primary transition-all duration-[120ms] focus:bg-white/50" onClick={() => setShowCalendar(!showCalendar)}>
                        {selectedDate ? selectedDate.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                  }) : <span className="text-muted-foreground">Pick a date</span>}
                      </Button>
                      {showCalendar && <div className="mt-2 p-0 border border-border rounded-lg shadow-medium bg-white">
                          <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={date => {
                      setSelectedDate(date);
                      field.onChange(date?.toISOString() || "");
                      setShowCalendar(false);
                    }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus /></SafeHydrate>
                        </div>}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <div className="mt-8 text-center" style={{
        opacity: formVisible ? 1 : 0,
        transform: formVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 350ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 350ms"
      }}>
          <Button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-9 has-[>svg]:px-3 text-foreground-primary px-8 py-6 text-base font-semibold rounded-md shadow-soft transition-all duration-[80ms] !bg-[hsl(22,78%,26%)] disabled:pointer-events-none dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-medium hover:-translate-y-0.5 disabled:opacity-50">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Booking...
              </> : "Book Consultation"}
          </Button>
        </div>
      </Form>
    </div>;
}